/**
 * `/casos/:id/historico` (E137.F3, GUS-160, ADR-0093): o histórico do caso na área da equipe, com
 * a verificação da trilha e a correção de um registro. O link sai da esteira do caso. Desde a
 * E138.F3 (GUS-21, ADR-0094), os documentos emitidos e a conferência de um arquivo vêm antes.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { HistoricoDoCaso } from '@/components/HistoricoDoCaso/HistoricoDoCaso';
import { DocumentosEmitidos } from '@/components/IntegridadeDoArquivo/DocumentosEmitidos';
import { ProximoPasso } from '@/components/ProximoPasso/ProximoPasso';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  HistoricoDoCasoError,
  lerHistoricoDoCaso,
  type HistoricoDoCasoData,
} from '@/lib/historico_do_caso';

const t = ptBR.historicoDoCaso;

export function HistoricoDoCasoPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const [historico, setHistorico] = useState<HistoricoDoCasoData | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async (): Promise<void> => {
    try {
      setHistorico(await lerHistoricoDoCaso(client, caseId));
      setErro(null);
    } catch (err) {
      const semPermissao = err instanceof HistoricoDoCasoError && err.motivo === 'sem_permissao';
      setErro(semPermissao ? t.semPermissao : t.erro);
    }
  }, [client, caseId]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  return (
    <section
      className="mx-auto flex max-w-4xl flex-col gap-sc-6 p-sc-6"
      data-testid="historico-page"
    >
      <header>
        <h1 className="text-display-sm text-fg">{t.titulo}</h1>
        <p className="mt-1 text-body-sm text-fg-muted">{t.subtitulo}</p>
        <Link className="mt-sc-2 inline-block text-body-sm text-accent-700" to={`/casos/${caseId}`}>
          {t.voltar}
        </Link>
      </header>
      <ProximoPasso client={client} caseId={caseId} />
      {erro !== null ? (
        <p role="alert" className="text-body-sm text-danger" data-testid="historico-erro">
          {erro}
        </p>
      ) : historico === null ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : (
        <>
          <DocumentosEmitidos client={client} caseId={caseId} />
          <HistoricoDoCaso
            client={client}
            caseId={caseId}
            historico={historico}
            onCorrigido={carregar}
          />
        </>
      )}
    </section>
  );
}
